import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import useFeatureCheck from "../../../customHooks/useFeatureCheck";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Col, DatePicker, Input, message, Row, Select } from "antd";

const { TextArea } = Input;
import { DeleteOutlined, DeleteFilled } from "@ant-design/icons";
import moment from "moment";
import { INPUT_SIZE } from "../../../common/ThemeConstants";
import FormSelect from "../../../components/general/FormSelect";
import FormTextField from "../../../components/general/FormTextField";
import { postRequest } from "../../../services/mainApp.service";
import { clampQtyRateValue } from "../../../utils/commonfunction";
import { TRANSFER } from "../../../common/InventoryEnum";
import BasicFormComponentInvTransaction from "../../../components/formComponent/BasicFormComponentInvTransaction";

const initialFormValues = {
  TransactionMasterId: null,
  TransactionNo: "",
  TransactionDate: moment().format("YYYY-MM-DD"),
  ReferenceNo: "",
  Description: "",
  TransactionTypeId: TRANSFER,
  BranchId: null,
  BranchToId: null,
  DepartmentId: null,
  DepartmentToId: null,
  Gross: 0,
  Tax: 0,
  Discount: 0,
  Amount: 0,
  ParentTransactionMasterId: null,
  IsEnable: true,
  IsSubmit: false,
  TransactionDetails: [],
  TotalAmount: 0,
};

const CreateEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const controller = new window.AbortController();
  const userData = useSelector((state) => state.authReducer);
  const [tableLoading, setTableLoading] = useState(false);
  const isEditMode = !!id;
  const [formFields, setFormFields] = useState(initialFormValues);
  const [formLoading, setFormLoading] = useState(false);
  const [supportingTable, setSupportingTable] = useState({});
  const [transactionDetails, setTransactionDetails] = useState([]);
  const [selectedItemForAdd, setSelectedItemForAdd] = useState(null);
  const [addDetailQty, setAddDetailQty] = useState("");
  const [addDetailTax, setAddDetailTax] = useState("");
  const [addDetailDiscount, setAddDetailDiscount] = useState("");
  const itemSelectRef = useRef(null);
  const itemsFetchedRef = useRef(null);
  const checkFeatureAuth = useFeatureCheck();

  useEffect(() => {
    fetchData();

    return () => {
      controller.abort();
    };
  }, [id]);

  useEffect(() => {
    const branches = supportingTable?.Branches || [];
    const departments = supportingTable?.Departments || [];
    setFormFields((prev) => {
      let next = { ...prev };
      if (branches.length === 1 && (prev.BranchId == null || prev.BranchId === "")) {
        next.BranchId = branches[0].Id;
      }
      if (departments.length === 1 && (prev.DepartmentId == null || prev.DepartmentId === "")) {
        next.DepartmentId = departments[0].Id;
      }
      return next;
    });
  }, [supportingTable?.Branches, supportingTable?.Departments]);

  // Fetch items when both Branch and Department are selected
  useEffect(() => {
    if (formFields.BranchId && formFields.DepartmentId && supportingTable?.Branches?.length > 0) {
      const currentKey = `${formFields.BranchId}-${formFields.DepartmentId}`;
      if (!itemsFetchedRef.current || itemsFetchedRef.current !== currentKey) {
        fetchItems();
        itemsFetchedRef.current = currentKey;
      }
    } else {
      setSupportingTable((prev) => ({
        ...prev,
        Items: [],
      }));
      itemsFetchedRef.current = null;
    }
  }, [formFields.BranchId, formFields.DepartmentId, supportingTable?.Branches]);

  const fetchItems = async () => {
    if (!formFields.BranchId || !formFields.DepartmentId) {
      return;
    }

    setTableLoading(true);
    const payload = {
      OperationId: 1,
      CompanyId: userData.CompanyId,
      UserId: userData.UserId,
      UserIP: "12.1.1.2",
      TransactionTypeId: TRANSFER,
      BranchId: formFields.BranchId,
      DepartmentId: formFields.DepartmentId,
    };

    try {
      const response = await postRequest("/Inv_Transaction", payload, controller);
      setTableLoading(false);

      if (response.error) {
        message.error(response.errorMessage);
        return;
      }

      if (response.data.response === false) {
        message.error(
          response.data.DataSet?.Table?.errorMessage || "Error fetching items"
        );
        return;
      }

      const dataSet = response.data;

      if (dataSet.AdditionalData || dataSet.DataSet) {
        setSupportingTable((prev) => ({
          ...prev,
          Items: dataSet.AdditionalData?.Items || dataSet.DataSet?.Table3 || [],
        }));
      }
    } catch (error) {
      setTableLoading(false);
      if (error.message !== "canceled") {
        console.error("Error fetching items:", error);
        message.error("Failed to fetch items");
      }
    }
  };

  const fetchData = async () => {
    setTableLoading(true);
    const payload = {
      OperationId: 1,
      CompanyId: userData.CompanyId,
      UserId: userData.UserId,
      UserIP: "12.1.1.2",
      TransactionTypeId: TRANSFER,
      TransactionMasterId: isEditMode ? parseInt(id) : null,
      IsDetailData: isEditMode ? true : false,
    };

    try {
      const response = await postRequest("/Inv_Transaction", payload, controller);
      setTableLoading(false);

      if (response.error) {
        message.error(response.errorMessage);
        return;
      }

      if (response.data.response === false) {
        message.error(
          response.data.DataSet?.Table?.errorMessage || "Error fetching data"
        );
        return;
      }
      const dataSet = response.data;

      if (isEditMode) {
        const masterData = dataSet?.Data?.[0] || {};
        setFormFields((prev) => ({ ...prev, ...masterData }));
        const detailsData = masterData?.TransactionDetails || [];
        setTransactionDetails(detailsData);
      }

      if (dataSet.AdditionalData || dataSet.DataSet) {
        setSupportingTable({
          Branches: dataSet.AdditionalData?.Branches || dataSet.DataSet?.Table1 || [],
          Departments: dataSet.AdditionalData?.Departments || dataSet.DataSet?.Table2 || [],
          TransactionNo: dataSet?.AdditionalData?.TransactionCode?.[0]?.Code || "",
          Items: dataSet.AdditionalData?.Items || dataSet.DataSet?.Table3 || dataSet.Data || [],
        });
      }
    } catch (error) {
      setTableLoading(false);
      if (error.message !== "canceled") {
        console.error("Error fetching transactions:", error);
        message.error("Failed to fetch transactions");
      }
    }
  };

  const handleFormChange = (data) => {
    setFormFields((prev) => {
      const updated = { ...prev, [data.name]: data.value };

      if (data.name === "BranchId" || data.name === "DepartmentId") {
        setTransactionDetails([]);
      }

      // Clear BranchToId if it matches the selected BranchId
      if (data.name === "BranchId" && prev.BranchToId === data.value) {
        updated.BranchToId = null;
      }

      return updated;
    });
  };

  const handleDateChange = (date) => {
    setFormFields((prev) => ({
      ...prev,
      TransactionDate: date ? date.format("YYYY-MM-DD") : null,
    }));
  };

  const itemList = supportingTable?.Items || [];

  const handleAddDetail = () => {
    const qty = clampQtyRateValue(addDetailQty);
    const tax = parseFloat(addDetailTax) || 0;
    const discount = parseFloat(addDetailDiscount) || 0;

    if (selectedItemForAdd) {
      const itemId = selectedItemForAdd.ItemId ?? selectedItemForAdd.Id;
      const isDuplicate = transactionDetails.some(
        (d) => (d.ItemId ?? d.Id) != null && (d.ItemId ?? d.Id) === itemId
      );
      if (isDuplicate) {
        message.error("This item is already added to the table. Duplicate items are not allowed.");
        return;
      }

      // Validate quantity against available stock in Issuance Unit
      const stockIU = selectedItemForAdd.StockIU ?? 0;
      if (qty > stockIU) {
        message.error(`Quantity cannot exceed available stock. Available stock: ${stockIU.toFixed(2)} ${selectedItemForAdd.IssuanceUnit ?? selectedItemForAdd.UnitName ?? ""}`);
        return;
      }

      if (qty <= 0) {
        message.error("Quantity must be greater than zero.");
        return;
      }

      const newDetail = {
        ItemId: itemId,
        ItemName: selectedItemForAdd.ItemName ?? selectedItemForAdd.Name,
        PurchaseUnitId: selectedItemForAdd.PurchaseUnitId ?? selectedItemForAdd.UnitId ?? null,
        PurchaseUnit: selectedItemForAdd.PurchaseUnit ?? selectedItemForAdd.UnitName ?? "",
        QtyPU: 0,
        RatePU: 0,
        TaxPU: 0,
        DiscountPU: 0,
        IssuanceUnitId: selectedItemForAdd.IssuanceUnitId ?? selectedItemForAdd.UnitId ?? null,
        IssuanceUnit: selectedItemForAdd.IssuanceUnit ?? selectedItemForAdd.UnitName ?? "",
        QtyIU: qty,
        RateIU: 0,
        TaxIU: tax,
        DiscountIU: discount,
        RecipeUnitId: null,
        QtyRU: 0,
        RateRU: 0,
        IsEnable: true,
        LastPurchaseRateIU: selectedItemForAdd.LastPurchaseRateIU ?? selectedItemForAdd.Rate ?? 0,
        StockIU: selectedItemForAdd.StockIU ?? 0,
        StockPU: selectedItemForAdd.StockPU ?? 0,
      };
      setTransactionDetails([newDetail, ...transactionDetails]);
      setSelectedItemForAdd(null);
      setAddDetailQty("");
      setAddDetailTax("");
      setAddDetailDiscount("");
      setTimeout(() => itemSelectRef.current?.focus(), 0);
    }
  };

  const handleDetailChange = (index, field, value) => {
    if (field === "QtyIU") {
      value = clampQtyRateValue(value);
    }
    const updatedDetails = [...transactionDetails];

    updatedDetails[index] = {
      ...updatedDetails[index],
      [field]: value,
    };

    if (field === "QtyIU") {
      updatedDetails[index].QtyIU = updatedDetails[index].QtyIU || 0;
    }
    if (field === "TaxIU" || field === "DiscountIU") {
      updatedDetails[index].TaxIU = updatedDetails[index].TaxIU ?? 0;
      updatedDetails[index].DiscountIU = updatedDetails[index].DiscountIU ?? 0;
    }

    setTransactionDetails(updatedDetails);
  };

  const handleRemoveDetail = (index) => {
    const updatedDetails = transactionDetails.filter((_, i) => i !== index);
    setTransactionDetails(updatedDetails);
  };

  const calculateTotals = () => {
    let gross = 0;
    let taxTotal = 0;
    let discountTotal = 0;
    transactionDetails.forEach((detail) => {
      const lineAmount = (detail.QtyIU || 0) * (detail.RateIU || 0);
      const tax = parseFloat(detail.TaxIU) || 0;
      const discount = parseFloat(detail.DiscountIU) || 0;
      gross += lineAmount;
      taxTotal += tax;
      discountTotal += discount;
    });
    const netAmount = gross + taxTotal - discountTotal;

    setFormFields((prev) => ({
      ...prev,
      Gross: gross,
      Tax: taxTotal,
      Discount: discountTotal,
      Amount: netAmount,
      TotalAmount: netAmount,
    }));
  };

  useEffect(() => {
    calculateTotals();
  }, [transactionDetails, formFields.Tax, formFields.Discount]);

  const handleFormSave = async (e, operationId, isSubmitFlag) => {
    e.preventDefault();

    if (!formFields.BranchId) {
      message.error("Please select a branch");
      return;
    }

    if (!formFields.BranchToId) {
      message.error("Please select a branch to");
      return;
    }

    if (!formFields.DepartmentId) {
      message.error("Please select a department");
      return;
    }

    if (transactionDetails.length === 0) {
      message.error("Please add at least one transaction detail");
      return;
    }

    // Validate stock quantity on Submit
    if (isSubmitFlag) {
      const stockInvalidItems = transactionDetails.filter((detail) =>
        detail.StockIU !== undefined &&
        parseFloat(detail.QtyIU) > parseFloat(detail.StockIU));

      if (stockInvalidItems.length > 0) {
        const invalidItemDetails = stockInvalidItems
          .map((item) => {
            return `${item.ItemName || "Unknown Item"} (Quantity: ${item.QtyIU.toFixed(2)}, Available Stock: ${item.StockIU.toFixed(2)} ${item.IssuanceUnit || ""})`;
          })
          .join("; ");
        message.error(
          `Quantity exceeds available stock for the following items: ${invalidItemDetails}. Please adjust the quantities before submitting.`
        );
        return;
      }
    }

    setFormLoading(true);

    const payload = {
      OperationId: operationId,
      CompanyId: userData.CompanyId,
      UserId: userData.UserId,
      UserIP: "12.1.1.2",
      TransactionMasterId: isEditMode ? parseInt(id) : null,
      TransactionNo: formFields.TransactionNo,
      TransactionDate: formFields.TransactionDate,
      ReferenceNo: formFields.ReferenceNo,
      Description: formFields.Description ?? "",
      TransactionTypeId: TRANSFER,
      BranchId: formFields.BranchId,
      BranchToId: formFields.BranchToId,
      DepartmentId: formFields.DepartmentId,
      DepartmentToId: formFields.DepartmentToId,
      Gross: formFields.Gross || 0,
      Tax: formFields.Tax || 0,
      Discount: formFields.Discount || 0,
      Amount: formFields.Amount || 0,
      ParentTransactionMasterId: formFields.ParentTransactionMasterId || null,
      IsEnable: formFields.IsEnable,
      IsSubmit: isSubmitFlag || formFields.IsSubmit,
      TransactionDetails: transactionDetails.map((detail) => ({
        ItemId: detail.ItemId,
        PurchaseUnitId: detail.PurchaseUnitId || null,
        QtyPU: detail.QtyPU || 0,
        RatePU: detail.RatePU || 0,
        TaxPU: detail.TaxPU || 0,
        DiscountPU: detail.DiscountPU || 0,
        IssuanceUnitId: detail.IssuanceUnitId || null,
        QtyIU: detail.QtyIU || 0,
        RateIU: detail.RateIU || 0,
        TaxIU: detail.TaxIU || 0,
        DiscountIU: detail.DiscountIU || 0,
        RecipeUnitId: detail.RecipeUnitId || null,
        QtyRU: detail.QtyRU || 0,
        RateRU: detail.RateRU || 0,
        IsEnable: detail.IsEnable !== false,
      })),
    };

    try {
      const response = await postRequest("/Inv_Transaction", payload, controller);
      setFormLoading(false);

      if (response.error) {
        message.error(response.errorMessage);
        return;
      }

      if (response.data.response === false) {
        message.error(
          response.data.DataSet?.Table?.errorMessage ||
          `Error ${isEditMode ? "updating" : "creating"} transaction`
        );
        return;
      }

      message.success(
        response.successMessage ||
        `Transaction ${isEditMode ? "updated" : "created"} successfully`
      );
      navigate("/transfer");
    } catch (error) {
      setFormLoading(false);
      if (error.message !== "canceled") {
        console.error("Error saving transaction:", error);
        const apiError =
          error.response?.data?.Error_Message ||
          error.response?.data?.Message ||
          error.response?.data?.errorMessage ||
          error.response?.data?.ResponseMessage;
        message.error(
          apiError || `Failed to ${isEditMode ? "update" : "create"} transfer`
        );
      }
    }
  };

  const detailColumns = useMemo(
    () => [
      { title: "Item Name", dataIndex: "ItemName", key: "ItemName" },
      { title: "Issuance Unit", dataIndex: "IssuanceUnit", key: "IssuanceUnit" },
      { title: "Stock Quantity", dataIndex: "StockIU", key: "StockIU" },
      {
        title: "Qty",
        dataIndex: "QtyIU",
        key: "QtyIU",
        width: 100,
        render: (_, record, index) => (
          <Input
            type="number"
            min={0}
            value={record.QtyIU}
            onChange={(e) =>
              handleDetailChange(index, "QtyIU", parseFloat(e.target.value) || 0)
            }
            style={{ width: "100%" }}
            readOnly={formFields.IsSubmit}
          />
        ),
      },
      // {
      //   title: "Net Amount",
      //   key: "NetAmount",
      //   width: 120,
      //   align: "right",
      //   render: (_, record) => {
      //     const qty = record.QtyIU ?? 0;
      //     const rate = record.RateIU ?? 0;
      //     const amount = (parseFloat(qty) || 0) * (parseFloat(rate) || 0);
      //     const tax = parseFloat(record.TaxIU) || 0;
      //     const discount = parseFloat(record.DiscountIU) || 0;
      //     const netAmount = amount + tax - discount;
      //     return netAmount.toFixed(2);
      //   },
      // },
      ...(!formFields.IsSubmit
        ? [
          {
            title: "Action",
            key: "actions",
            width: 100,
            render: (_, record, index) => (
              <Button
                type="link"
                danger
                icon={<DeleteFilled />}
                onClick={() => handleRemoveDetail(index)}
              />
            ),
          },
        ]
        : []),
    ],
    [formFields.IsSubmit, handleDetailChange, handleRemoveDetail, transactionDetails]
  );

  const formPanel = (
    <Fragment>
      <FormTextField
        colSpan={4}
        label="Transaction No"
        name="TransactionNo"
        size={INPUT_SIZE}
        value={isEditMode ? formFields.TransactionNo : supportingTable?.TransactionNo}
        readOnly={true}
      />

      <Col span={4}>
        <label style={{ display: "block", marginBottom: 5 }}>
          Transaction Date <span style={{ color: "red" }}>*</span>
        </label>
        {!formFields.IsSubmit ? (
          <>
            <DatePicker
              style={{ width: "100%" }}
              value={formFields.TransactionDate ? moment(formFields.TransactionDate) : null}
              onChange={handleDateChange}
              format="YYYY-MM-DD"
            />
          </>
        ) : (
          <>
            <FormTextField
              name="TransactionDate"
              size={INPUT_SIZE}
              value={formFields.TransactionDate ? moment(formFields.TransactionDate).format("YYYY-MM-DD") : null}
              readOnly={true}
            />
          </>
        )}
      </Col>

      <FormSelect
        colSpan={4}
        listItem={supportingTable.Branches || []}
        idName="Id"
        valueName="Name"
        size={INPUT_SIZE}
        name="BranchId"
        label="Branch"
        value={formFields.BranchId}
        onChange={handleFormChange}
        required={true}
        disabled={formFields.IsSubmit}
      />

      <FormSelect
        colSpan={4}
        listItem={supportingTable.Departments || []}
        idName="Id"
        valueName="Name"
        size={INPUT_SIZE}
        name="DepartmentId"
        label="Department"
        value={formFields.DepartmentId}
        onChange={handleFormChange}
        required={true}
        disabled={formFields.IsSubmit}
      />

      <FormSelect
        colSpan={4}
        listItem={(supportingTable.Branches || []).filter(branch => branch.Id !== formFields.BranchId)}
        idName="Id"
        valueName="Name"
        size={INPUT_SIZE}
        name="BranchToId"
        label="Branch To"
        value={formFields.BranchToId}
        onChange={handleFormChange}
        required={true}
        disabled={formFields.IsSubmit || !formFields.BranchId}
        placeholder={!formFields.BranchId ? "Select Branch first" : "Select branch to"}
      />

      <FormSelect
        colSpan={4}
        listItem={supportingTable.Departments || []}
        idName="Id"
        valueName="Name"
        size={INPUT_SIZE}
        name="DepartmentToId"
        label="Department To"
        value={formFields.DepartmentToId}
        onChange={handleFormChange}
        required={true}
        disabled={formFields.IsSubmit}
      />

      <Col span={8}>
        <label style={{ display: "block", marginBottom: 5 }}>Description</label>
        <TextArea
          name="Description"
          value={formFields.Description ?? ""}
          onChange={(e) => handleFormChange({ name: "Description", value: e.target.value })}
          rows={1}
          placeholder="Enter description"
          readOnly={formFields.IsSubmit}
          disabled={formFields.IsSubmit}
          style={{ width: "100%" }}
        />
      </Col>

      <FormTextField
        colSpan={4}
        label="Total Amount"
        name="TotalAmount"
        size={INPUT_SIZE}
        value={formFields.TotalAmount}
        type="text"
        isNumber="true"
        onChange={handleFormChange}
        readOnly={true}
      />
      <FormTextField
        colSpan={4}
        label="Total Items"
        name="TotalItems"
        size={INPUT_SIZE}
        value={transactionDetails.length}
        type="text"
        onChange={handleFormChange}
        readOnly={true}
      />
    </Fragment>
  );

  const getItemId = (item) => item?.ItemId ?? item?.Id;
  const getItemName = (item) => (item?.ItemName ?? item?.Name ?? "") + " ( " + (item?.IssuanceUnit ?? item?.UnitName ?? "") + " ) ";
  const getItemCode = (item) => item?.ItemCode ?? item?.Code ?? "";
  const getBarcode = (item) => item?.Barcode ?? item?.BarCode ?? "";
  const getStockIU = (item) => item?.StockIU ?? item?.Stock ?? 0;

  const labelStyle = {
    display: "block",
    marginBottom: 5,
    fontSize: 14,
    fontWeight: 500,
    color: "#262626",
  };

  const addDetailEntryPanel = (
    <>
      {!formFields.IsSubmit && (
        <div
          style={{
            width: "100%",
            padding: "16px",
            background: "#fafafa",
            borderRadius: 8,
            border: "1px solid #f0f0f0",
            marginBottom: 16,
          }}
        >
          <div
            style={{
              marginBottom: 12,
              fontSize: 14,
              fontWeight: 600,
              color: "#262626",
            }}
          >
            Add Item Detail
          </div>
          <Row gutter={[16, 12]} align="bottom">
            <Col xs={24} sm={12} md={6} lg={5}>
              <label style={labelStyle}>Bar code</label>
              <Select
                style={{ width: "100%" }}
                placeholder="Bar code"
                showSearch
                allowClear
                size="middle"
                disabled={!formFields.BranchId || !formFields.DepartmentId}
                value={selectedItemForAdd ? getBarcode(selectedItemForAdd) : undefined}
                onChange={(value) => {
                  const item = itemList.find((i) => getBarcode(i) === value);
                  setSelectedItemForAdd(item || null);
                }}
                filterOption={(input, option) =>
                  (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                }
                options={[
                  ...itemList
                    .filter((i) => getBarcode(i))
                    .map((item) => ({
                      value: getBarcode(item),
                      label: getBarcode(item),
                    })),
                ]}
              />
            </Col>
            <Col xs={24} sm={12} md={6} lg={5}>
              <label style={labelStyle}>Item code</label>
              <Select
                style={{ width: "100%" }}
                placeholder="Item code"
                showSearch
                allowClear
                size="middle"
                disabled={!formFields.BranchId || !formFields.DepartmentId}
                value={selectedItemForAdd ? getItemCode(selectedItemForAdd) : undefined}
                onChange={(value) => {
                  const item = itemList.find((i) => getItemCode(i) === value);
                  setSelectedItemForAdd(item || null);
                }}
                filterOption={(input, option) =>
                  (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                }
                options={[
                  ...itemList
                    .filter((i) => getItemCode(i))
                    .map((item) => ({
                      value: getItemCode(item),
                      label: getItemCode(item),
                    })),
                ]}
              />
            </Col>
            <Col xs={24} sm={12} md={6} lg={5}>
              <label style={labelStyle}>Item name</label>
              <Select
                ref={itemSelectRef}
                style={{ width: "100%" }}
                placeholder={
                  !formFields.BranchId || !formFields.DepartmentId
                    ? "Select Branch and Department first"
                    : "Search by item name"
                }
                showSearch
                allowClear
                size="middle"
                disabled={!formFields.BranchId || !formFields.DepartmentId}
                value={selectedItemForAdd ? getItemId(selectedItemForAdd) : undefined}
                onChange={(value) => {
                  const item = itemList.find((i) => getItemId(i) === value);
                  setSelectedItemForAdd(item || null);
                }}
                filterOption={(input, option) =>
                  (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                }
                options={[
                  ...itemList.map((item) => ({
                    value: getItemId(item),
                    label: getItemName(item),
                  })),
                ]}
              />
            </Col>
            <Col xs={12} sm={8} md={4} lg={3}>
              <label style={labelStyle}>Available stock</label>
              <Input
                readOnly
                disabled
                size="middle"
                value={selectedItemForAdd ? getStockIU(selectedItemForAdd).toFixed(2) : "—"}
                style={{
                  background: "#fff",
                  cursor: "default",
                  color: "#595959",
                }}
              />
            </Col>
            <Col xs={12} sm={8} md={4} lg={3}>
              <label style={labelStyle}>Quantity</label>
              <Input
                type="number"
                placeholder="0"
                size="middle"
                value={addDetailQty}
                onChange={(e) => setAddDetailQty(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleAddDetail();
                }}
                min={0}
                step={1}
                disabled={!formFields.BranchId || !formFields.DepartmentId}
              />
            </Col>
            <Col xs={24} sm={24} md={24} lg={24}>
              <Button
                type="primary"
                onClick={handleAddDetail}
                disabled={!selectedItemForAdd || !addDetailQty || !formFields.BranchId || !formFields.DepartmentId}
                style={{ marginTop: 8 }}
              >
                Add Item
              </Button>
            </Col>
          </Row>
        </div>
      )}
    </>
  );

  return (
    <BasicFormComponentInvTransaction
      pageMode="transaction"
      isEditMode={isEditMode}
      formTitle={"Transfer" + (formFields.IsSubmit ? " - ( Posted )" : "")}
      crudTitle={"Transfer" + (formFields.IsSubmit ? " - ( Posted )" : "")}
      baseRoute="/transfer"
      formPanel={formPanel}
      transactionDetailsColumns={detailColumns}
      transactionDetailsRows={transactionDetails}
      onAddDetail={handleAddDetail}
      onFormSave={handleFormSave}
      showSubmit={!formFields.IsSubmit && checkFeatureAuth("POST", "Transfer", { showErrorMessage: false })}
      formLoading={formLoading}
      submitFormLoading={formLoading}
      onCancelForm={() => navigate("/transfer")}
      showSave={!formFields.IsSubmit}
      showAddDetailButton={false}
      addDetailEntryPanel={addDetailEntryPanel}
    />
  );
};

export default CreateEdit;
